import { useEffect, useState } from "react"
import { DateTime } from "luxon"
import { ReadMonitorRunResultParams, useReadMonitorRunResultGroupedByMonitorIdQuery } from "services/monitorRunResult"
import { Box, SxProps } from "@mui/material"
import { ChartDemand } from "../../presentationals/ChartDemand"
import { ChartDatePanel } from "../../presentationals/ChartDatePanel"
import { ButtonStyle, ChartDateMode} from "../../presentationals/ChartDatePanel/ChartDatePanel"
import { median } from "shared/lib/median"
import { compareLuxonDates } from "shared/lib/compareLuxonDates"
import { useReadMonitorByIdQuery } from "services/monitor"
import { DateTimePeriod } from "shared/types/DateTimePeriod"
import { ChartHeader } from "../../presentationals/ChartHeader"

interface ChartDemandContainerProps {
    spyId?: number
    filter?: any
    expanded: boolean
    onExpand: (event: any) => void
    onCollapse: (event: any) => void
}

export const ChartDemandContainer = (props: ChartDemandContainerProps): JSX.Element => {
    const {
        spyId,
        filter,
        expanded,
        onExpand,
        onCollapse
    } = props

    const [readMonitorRunResultByMonitorIdQueryParams, setReadMonitorRunResultByMonitorIdQueryParams] = useState<ReadMonitorRunResultParams>({
        monitorId: spyId,
    })

    const [readMonitorRunResultByMonitorIdQueryOptions, setReadMonitorRunResultByMonitorIdQueryOptions] = useState<any>({
        skip: true,
        refetchOnMountOrArgChange: true,
        pollingInterval: 5000,
    })

    useEffect(() => {
        setReadMonitorByIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: spyId
            }
        });

        setReadMonitorByIdOptions((prevValue: any) => {
            return {
                    ...prevValue,
                    skip: spyId === undefined,
            }
        })
    }, [
        spyId
    ])

    useEffect(() => {
        setReadMonitorRunResultByMonitorIdQueryParams(
            {
                monitorId: spyId,
                // ...filter
            }
        )

        setReadMonitorRunResultByMonitorIdQueryOptions({
            skip: spyId === undefined,
            refetchOnMountOrArgChange: true,
            pollingInterval: 5000,
        })

    }, [
        spyId,
        filter
    ])

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultByMonitorIdQueryParams,
        readMonitorRunResultByMonitorIdQueryOptions
        );

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined)
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    );

    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions)

    const [chartDateMode, setChartDateMode] = useState<ChartDateMode | undefined>(undefined)
    const [chartDateTimePeriod, setChartDateTimePeriod] = useState<DateTimePeriod | undefined>(undefined)
    const [data, setData] = useState<any>()
    const [toDemandGrow, setToDemandGrow] = useState<number>()
    const [toDemandGrowPercent, setToDemandGrowPercent] = useState<number>()
    const [demandMarketDirection, setDemandMarketDirection] = useState<number | undefined>(undefined)
    const [demandMarketDirectionGrow, setDemandMarketDirectionGrow] = useState<number | undefined>(undefined)
    const [demandMarketDirectionGrowPercent, setDemandMarketDirectionGrowPercent] = useState<number | undefined>(undefined)
    const [chartDemandData, setChartDemandData] = useState<Object | undefined>()
    const [chartDemandOptions, setChartDemandOptions] = useState<any>({
        elements: {
            point: {
                radius: 5,
                hoverRadius: 7,
                hitRadius: 16
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {

                ticks: {
                    color: "#000000",
                    precision: 0,
                    beginAtZero: true
                }
            },
            x: {

                type: "timeseries",
                stacked: true,
                ticks: {
                    color: "#000000",
                    maxTicksLimit: 6
                },
                time: {
                    displayFormats: {
                        day: 'MM/dd/yyyy',
                        hour: 'MM/dd/yyyy',
                        minute: 'MM/dd/yyyy',
                    }
                },
                adapters: {
                    date: {
                        zone: 'utc'
                    }
                }
            }
        },
        plugins: {
            title: {
                display: false,
                text: 'Demand',
                font: {
                    size: 16 
                }
            },
            legend: false,
            annotation: {
                annotations: {
                    label1: {
                        display: (obj: any, args: any, options: any) => {
                            return !obj.chart.data?.datasets.some((dataset: any) => dataset.data.length > 0)
                        },
                        type: 'label',
                        position: 'center',
                        backgroundColor: 'rgba(0,0,0, 0)',
                        content: ['No data available'],
                        font: {
                            size: 36,
                            weight: 600
                        }
                    }
                }
            }
        }
    })

    useEffect(() => {
        if (readMonitorByIdQueryData === undefined) {
            return
        }

        if (readMonitorByIdQueryData.customPeriods && readMonitorByIdQueryData.customPeriods.length > 0) {
            const startDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].startDateTimeUtc, { zone: "UTC" }).startOf('day')
            const endDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].endDateTimeUtc, { zone: "UTC" }).endOf('day')


            setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
                setChartDateTimePeriod({
                    from: startDate,
                    to: endDate
                })
            })

            setChartDateMode('Custom')

            return
        }

        setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
            setChartDateTimePeriod({
                from: DateTime.now().startOf('day'),
                to: DateTime.now().endOf('day').plus({ month: 1 })
            })
        })

        setChartDateMode('1M')
    },
        [
            readMonitorByIdQueryData
        ]
    )

    useEffect(() => {
        if (readMonitorRunResultGroupedByMonitorIdData === undefined) {
            setData(undefined)

            return
        }

        const transformedData: any[] = readMonitorRunResultGroupedByMonitorIdData.list
            .map((resultItem: any, index: number, list: any) => {
                const checkinUtc = DateTime.fromISO(resultItem?.checkinUtc, { setZone: true });

                return {
                    checkinUtc: checkinUtc,
                    demand: resultItem.demand
                }
            })
            .sort(compareLuxonDates)

        let trData = transformedData;

        if (chartDateTimePeriod !== undefined) {
            const fromDt = DateTime.fromObject({
                year: chartDateTimePeriod.from.year,
                month: chartDateTimePeriod.from.month,
                day: chartDateTimePeriod.from.day
            }, { zone: "UTC" })

            const toDt = DateTime.fromObject({
                year: chartDateTimePeriod.to.year,
                month: chartDateTimePeriod.to.month,
                day: chartDateTimePeriod.to.day,
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 999

            }, { zone: "UTC" })

            trData = transformedData.filter((row: any) => {
                return row.checkinUtc >= fromDt && row.checkinUtc <= toDt && row.demand !== null
            })
        }

        setData(trData)
    }, [readMonitorRunResultGroupedByMonitorIdData, chartDateTimePeriod]);

    useEffect(() => {
        function cleanState() {
            setToDemandGrow(undefined)
            setToDemandGrowPercent(undefined)
            setDemandMarketDirection(undefined)
            setDemandMarketDirectionGrow(undefined)
            setDemandMarketDirectionGrowPercent(undefined)
            setChartDemandData({
                datasets: [],
            })
        }

        function handleChartDemandOptions() {
            setChartDemandOptions((prevValue: any) => {
                const scales = {
                    ...prevValue.scales
                }

                let maxValue = Math.max.apply(null, data.map((o: any) => { return o.demand; }));
                let minValue = Math.min.apply(null, data.map((o: any) => { return o.demand; }));

                scales.y.min = minValue - 10;
                scales.y.max = maxValue + 10;

                return ({
                    ...prevValue,
                    scales
                });
            })
        }

        function handleStateAndDatasets() {
            const datasets: any = []

            if (data && data.length > 0) {
                const startItem = data.find((row: any) => row.demand !== null)
                const endItem = data.findLast((row: any) => row.demand !== null)

                if (startItem === undefined || endItem === undefined) {
                    return
                }

                const startDemand = startItem.demand
                const endDemand = endItem.demand
                const demandGrow = endDemand - startDemand
                let percentGrow = undefined

                if (startDemand === 0) {
                    percentGrow = (demandGrow / 1) * 100
                } else {
                    percentGrow = (demandGrow / startDemand) * 100
                }

                if (demandGrow >= 0) {
                    percentGrow = Math.abs(percentGrow)
                }

                setToDemandGrow(demandGrow)
                setToDemandGrowPercent(percentGrow)

                const arr = data
                    .filter((row: any) => row.demand !== null)
                    .map((o: any) => { return (o.demand) })

                let diffDemandStartMedianSumm = undefined
                const medianResult = median(arr)
                if (medianResult !== undefined) {
                    diffDemandStartMedianSumm = medianResult - startDemand
                    let percentDemandMarketGrow: number | undefined = undefined

                    if (startDemand === 0) {
                        percentDemandMarketGrow = (diffDemandStartMedianSumm / 1) * 100
                    } else {
                        percentDemandMarketGrow = (diffDemandStartMedianSumm / startDemand) * 100
                    }

                    if (diffDemandStartMedianSumm >= 0) {
                        percentDemandMarketGrow = Math.abs(percentDemandMarketGrow)
                    }

                    setDemandMarketDirection(medianResult)
                    setDemandMarketDirectionGrow(diffDemandStartMedianSumm)
                    setDemandMarketDirectionGrowPercent(percentDemandMarketGrow)
                    const borderColor = diffDemandStartMedianSumm < 0 ? '#FE0000' : '#4caf50'

                    datasets.push(
                        {
                            fill: 'start',
                            type: 'line' as const,
                            label: 'Market direction',
                            data: [
                                {
                                    demandMarketDirectionGrow: startItem.demand,
                                    checkinUtc: startItem.checkinUtc
                                },
                                {
                                    demandMarketDirectionGrow: medianResult,
                                    checkinUtc: endItem.checkinUtc
                                }
                            ],
                            parsing: {
                                yAxisKey: 'demandMarketDirectionGrow',
                                xAxisKey: 'checkinUtc',
                            },
                            borderColor: borderColor,
                            backgroundColor: () => (`rgba(254, 0, 0, 0)`)
                        }
                    )
                }

                const negativeDirection = diffDemandStartMedianSumm !== undefined && diffDemandStartMedianSumm < 0
                const mainGrapBorderColor = negativeDirection ? '#FE0000' : '#4caf50'
                datasets.push({
                    fill: 'start',
                    type: 'line' as const,
                    label: 'Demand',
                    data: data,
                    parsing: {
                        yAxisKey: 'demand',
                        xAxisKey: 'checkinUtc',
                    },
                    borderColor: mainGrapBorderColor,
                    backgroundColor: function (context: any) {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;

                        if (!chartArea) {
                            return;
                        }

                        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                        if (negativeDirection) {
                            gradient.addColorStop(0, 'rgba(254, 0, 0, 0.2)');
                            gradient.addColorStop(1, 'rgba(254, 0, 0, 0.05)');
                        } else {
                            gradient.addColorStop(0, 'rgba(76, 176, 80, 0.2)');
                            gradient.addColorStop(1, 'rgba(76, 176, 80, 0.05)');
                        }

                        return gradient;
                    },
                })
            }

            setChartDemandData({
                datasets
            })
        }

        cleanState()

        if (data === undefined) {
            return
        }

        handleChartDemandOptions()
        handleStateAndDatasets()
    }, [data])

    const chartDatePanelButtonStyle: ButtonStyle = {
        color: {
            default: '#C0C0C0',
            hover: '#4CB050',
            selected: '#4CB050'
        },
        borderColor: {
            default: '#C0C0C0',
            hover: '#4CB050',
            selected: '#4CB050'
        }
    }

    const handleModeChange = (mode: ChartDateMode) => {
        setChartDateMode(mode)
    }

    const handleDateTimePeriodChange = (period: DateTimePeriod) => {
        setChartDateTimePeriod(period)
    }

    const namePropStyle = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 0 10px 0',
    }

    const propertyListContainer = {
        padding: '20px 0px',
        gridTemplateColumns: 'max-content max-content max-content',
        display: 'grid',

    }

    const valuePropStyle: SxProps = {
        margin: '0 0 10px 20px',
        fontSize: '20px',
        '&.CellValue__Root_isGrowing': {
            color: `#4CB050`
        },
        '&.CellValue__Root_isFalling': {
            color: `#FE0000`
        }
    }

    const renderValue = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(0)}`

        if (value < 0) {
            //classes = 'CellValue__Root_isFalling'
            handledValue = `-${Math.abs(value).toFixed(0)}`
        } else if (value > 0) {
            //classes = 'CellValue__Root_isGrowing'
            handledValue = `+${Math.abs(value).toFixed(0)}`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const renderValuePercent = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(2)}%`

        if (value < 0) {
            classes = 'CellValue__Root_isFalling'
            handledValue = `${value.toFixed(2)}%`
        } else if (value > 0) {
            classes = 'CellValue__Root_isGrowing'
            handledValue = `+${value.toFixed(2)}%`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const handleChartHeaderExpandButtonClick = (event: any) => {
        onExpand?.(event)
    }

    const handleChartHeaderCollapseButtonClick = (event: any) => {
        onCollapse?.(event)
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <ChartHeader 
                name="Demand"
                expanded={expanded}
                date={[chartDateTimePeriod?.from, chartDateTimePeriod?.to]}
                onExpandButtonClick={handleChartHeaderExpandButtonClick}
                onCollapseButtonClick={handleChartHeaderCollapseButtonClick}
            />
            <ChartDemand
                data={chartDemandData}
                options={chartDemandOptions}
                loading={chartDemandData === undefined}
            />

            <ChartDatePanel
                buttonStyle={chartDatePanelButtonStyle}
                mode={chartDateMode}
                onModeChange={handleModeChange}
                onDateTimePeriodChange={handleDateTimePeriodChange}
                dateTimePeriod={chartDateTimePeriod}
            />

            <Box sx={propertyListContainer}>
                <Box sx={namePropStyle}>
                    Demand:
                </Box>
                {
                    renderValue(toDemandGrow)
                }
                {
                    renderValuePercent(toDemandGrowPercent)
                }
                <Box sx={namePropStyle}>
                    Market direction:
                </Box>
                {
                    renderValue(demandMarketDirectionGrow)
                }
                {
                    renderValuePercent(demandMarketDirectionGrowPercent)
                }
            </Box>
        </Box>

    )
}